import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import {
	isHeadingItem,
	isPeopleAndTeamsItem,
	isRecentActivityItem,
	isSearchResultItem,
	isUserDocumentItem,
	type Item,
} from '../types';

type ItemsContextType = {
	invalidateItem: (id: string) => void;
	getItemIndexes: (id: string | undefined) =>
		| {
				globalIndex: number;
				sectionIndex: number;
				indexWithinSection: number;
				resultCount: number;
				sectionCount: number;
				sectionId: string;
		  }
		| undefined;
};

const ItemsContext = createContext<ItemsContextType | undefined>(undefined);

const isItemCountable = (item: Item) =>
	isSearchResultItem(item) ||
	isUserDocumentItem(item) ||
	isRecentActivityItem(item) ||
	isPeopleAndTeamsItem(item);

const isHeading = (item: Item) => isHeadingItem(item);

const getItemsAndSectionsCount = (allItems: Item[]) =>
	allItems.reduce(
		({ resultCount, sectionCount }, item, currentIndex) => {
			if (isItemCountable(item)) {
				resultCount++;
			}

			if (currentIndex === 0 && !isHeading(item)) {
				// if the first item is not heading, we consider it in a section
				sectionCount++;
			}

			if (isHeading(item)) {
				sectionCount++;
			}

			return { resultCount, sectionCount };
		},
		{ resultCount: 0, sectionCount: 0 },
	);

export const useItemsAnalyticsContext = () => {
	const ctx = useContext(ItemsContext);
	if (!ctx) {
		// We throw an error here to make sure the hook is used within the provider,
		// otherwise it would return undefined and cause runtime errors and is hard to debug
		throw new Error('useItemsAnalyticsContext must be used within ItemsAnalyticsProvider');
	}
	return ctx;
};

export const ItemsAnalyticsProvider: React.FC<{ allItems: Item[]; children: React.ReactNode }> = ({
	allItems,
	children,
}) => {
	const invalidatedIdsRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		// Reset invalidated IDs when allItems change, the items would call invalidateItem again if needed
		invalidatedIdsRef.current.clear();
	}, [allItems]);

	const invalidateItem = (id: string) => {
		invalidatedIdsRef.current.add(id);
	};

	const { resultCount, sectionCount } = useMemo(
		() => getItemsAndSectionsCount(allItems),
		[allItems],
	);

	const getItemIndexes = useCallback(
		(id: string | undefined) => {
			const item =
				id && !invalidatedIdsRef.current.has(id)
					? allItems.find((item) => item.id === id)
					: undefined;
			if (!item || !isItemCountable(item)) {
				return undefined;
			}

			let globalIndex = -1;
			let sectionIndex = -1;
			let indexWithinSection = -1;
			let sectionId = '';

			const activeItems = allItems.filter((item) => !invalidatedIdsRef.current.has(item.id));
			for (let i = 0; i < activeItems.length; i++) {
				if (isHeading(activeItems[i])) {
					sectionIndex++;
					indexWithinSection = -1;
					sectionId = activeItems[i].id;
				} else {
					if (i === 0) {
						// if the first item is not heading, we consider it in a section
						sectionIndex++;
					}
					if (isItemCountable(activeItems[i])) {
						indexWithinSection++;
						globalIndex++;
					}

					if (activeItems[i].id === id) {
						return {
							globalIndex,
							sectionIndex,
							indexWithinSection,
							resultCount,
							sectionCount,
							sectionId,
						};
					}
				}
			}

			// if there is no match, return undefined
			return undefined;
		},
		[allItems, resultCount, sectionCount],
	);

	return (
		<ItemsContext.Provider value={{ invalidateItem, getItemIndexes }}>
			{children}
		</ItemsContext.Provider>
	);
};
